import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { AiOutlineSafety, AiOutlineLock, AiOutlineUnlock } from 'react-icons/ai';
import { useT } from './LocaleContext';
import { GlobalStyle } from './styles/GlobalStyles';
import { authAPI, profileAPI } from './api';
import { clearProfileCache, getErrorMessage } from './common';
import {
  ModalOverlay,
  ModalContainer,
  ModalTitle,
  ModalTextContainer,
  ButtonContainer,
  ModalButton,
} from './styles/ProfilePageStyles';
import {
  ModalForm,
  ModalFormCodeInput,
  ModalFormStatus,
  ModalFormStatusMessage,
} from './styles/ModalFormStyles';
import { 
  AuthContainer, 
  Title, 
  StyledText, 
  Button, 
  PrimaryButton,
  ExitButton,
  ButtonGroup, 
  QRCodeImage, 
  SecurityIcon,
  StatusCard,
  StatusIcon,
  StatusText,
  LoadingSpinner
} from './styles/TwoFactorAuthPageStyles';


const TwoFactorAuthPage = () => {
  const t = useT();
  const [isEnabled, setIsEnabled] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingQR, setIsLoadingQR] = useState(false);


  const fetch2FAStatus = async () => {
    try {
      const response = await profileAPI.getProfile();
      setIsEnabled(response.data.is_2fa_enabled);
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('twoFa.loadError'));
      setMessage({ text: errorMessage, success: false });
    }
  };

  useEffect(() => {
    fetch2FAStatus();
  }, []);

  const handleSetup2FA = async () => {
    setIsModalOpen(true);
    setIsLoadingQR(true);
    setQrCodeUrl('');
    setCode('');
    setMessage(null);
    try {
      const response = await authAPI.twoFactorSetup();
      const url = URL.createObjectURL(new Blob([response.data]));
      setQrCodeUrl(url);
      setMessage(null);
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('twoFa.setupError'));
      setMessage({ text: errorMessage, success: false });
    } finally {
      setIsLoadingQR(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setQrCodeUrl('');
    setCode('');
    setMessage(null);
  };

  const handleVerifyCode = async (e) => {
    e?.preventDefault?.();
    if (!code) {
      setMessage({ text: t('twoFa.enterCode'), success: false });
      return;
    }

    try {
      await authAPI.twoFactorEnable(code.trim());
      flushSync(() => {
        setIsEnabled(true);
      });
      clearProfileCache();
      closeModal();
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('twoFa.verifyError'));
      setMessage({ text: errorMessage, success: false });
    }
  };

  const handleDisable2FA = async () => {
    try {
      await authAPI.twoFactorDisable();
      setMessage({ text: t('twoFa.disabledSuccess'), success: true });
      setIsEnabled(false);
      clearProfileCache();
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('twoFa.disableError'));
      setMessage({ text: errorMessage, success: false });
    }
  };

  return (
    <AuthContainer>
      <GlobalStyle />
      <SecurityIcon>
        <AiOutlineSafety />
      </SecurityIcon>
      <Title>{t('twoFa.title')}</Title>
      <StyledText>{t('twoFa.intro1')}</StyledText>
      <StyledText>{t('twoFa.intro2')}</StyledText>
      <StyledText>{t('twoFa.intro3')}</StyledText>
      <StyledText>{t('twoFa.intro4')}</StyledText>
      {isEnabled ? (
        <>
          <StatusCard success>
            <StatusIcon>
              <AiOutlineLock />
            </StatusIcon>
            <StatusText>{t('twoFa.enabled')}</StatusText>
          </StatusCard>
          <ButtonGroup>
            <Button onClick={handleDisable2FA}>{t('twoFa.disable')}</Button>
            <ExitButton to="/profile">{t('twoFa.exit')}</ExitButton>
          </ButtonGroup>
        </>
      ) : (
        <>
          <StatusCard>
            <StatusIcon>
              <AiOutlineUnlock />
            </StatusIcon>
            <StatusText>{t('twoFa.disabled')}</StatusText>
          </StatusCard>
          <ButtonGroup>
            <PrimaryButton onClick={handleSetup2FA}>{t('twoFa.setup')}</PrimaryButton>
            <ExitButton to="/profile">{t('twoFa.exit')}</ExitButton>
          </ButtonGroup>
        </>
      )}

      {isModalOpen && (
        <ModalOverlay>
          <ModalContainer>
            <ModalTextContainer>
              <ModalTitle>{t('twoFa.scanQr')}</ModalTitle>
            </ModalTextContainer>
            {isLoadingQR ? (
              <LoadingSpinner />
            ) : (
              qrCodeUrl && <QRCodeImage src={qrCodeUrl} alt="QR code for 2FA" />
            )}
            <ModalForm onSubmit={handleVerifyCode}>
              <ModalFormCodeInput
                type="text"
                value={code}
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder={t('common.code')}
              />
              <ModalFormStatus>
                <ModalFormStatusMessage success={message?.success}>
                  {message?.text || ''}
                </ModalFormStatusMessage>
              </ModalFormStatus>
              <ButtonContainer>
                <ModalButton type="button" onClick={closeModal}>{t('common.close')}</ModalButton>
                <ModalButton type="submit" primary>{t('twoFa.activate')}</ModalButton>
              </ButtonContainer>
            </ModalForm>
          </ModalContainer>
        </ModalOverlay>
      )}
    </AuthContainer>
  );
};

export default TwoFactorAuthPage;
